import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Phone, Search, Download, X, Save, Layers, AlertTriangle, ChevronLeft, ChevronRight, Loader2, FileSpreadsheet, Filter, MessageSquare, ChevronDown, Users, Activity, Upload, CheckCircle, AlertCircle, FileText, Sparkles, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { API_URL, NODE_API_URL } from '../config';

const Tracker = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // ✅ Separate loading states for the two buttons
    const [downloadingFiltered, setDownloadingFiltered] = useState(false);
    const [downloadingAll, setDownloadingAll] = useState(false);

    // ✅ 1. INITIALIZE STATE FROM SESSION STORAGE
    const [searchTerm, setSearchTerm] = useState(() => {
        return sessionStorage.getItem('tracker_search') || '';
    });

    const [entriesPerPage, setEntriesPerPage] = useState(() => {
        return parseInt(sessionStorage.getItem('tracker_entries')) || 10;
    });

    const [statusFilter, setStatusFilter] = useState(() => {
        return sessionStorage.getItem('tracker_status_filter') || 'ALL';
    });

    const [rxStatusFilter, setRxStatusFilter] = useState(() => {
        return sessionStorage.getItem('tracker_rx_status_filter') || 'ALL';
    });

    const [currentPage, setCurrentPage] = useState(() => {
        return parseInt(sessionStorage.getItem('tracker_page')) || 1;
    });

    // Modal States
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // Slide-out Details Drawer States
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPresc, setSelectedPresc] = useState(null);
    const [drawerEdits, setDrawerEdits] = useState({});
    const [drawerSaving, setDrawerSaving] = useState(false);
    const [highlightedRxId, setHighlightedRxId] = useState(null);

    // Sorting States
    const [sortField, setSortField] = useState('name'); // 'name' | 'date' | 'status' | 'pending'
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'

    const [editForm, setEditForm] = useState({
        id: null,
        prescriber_name: '',
        prescriber_phone: '',
        patient_name: '',
        patient_phone: '',
        dob: '',
        insurance_provider: '',
        latest_call_status: ''
    });

    // ===== Excel Importer State =====
    const [showImportModal, setShowImportModal] = useState(false);
    const [importStep, setImportStep] = useState(1); // 1 upload, 2 preview, 3 complete
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState('');
    const [importTempId, setImportTempId] = useState('');
    const [importPreview, setImportPreview] = useState([]);
    const [importPreviewPage, setImportPreviewPage] = useState(1);
    const [importPreviewEntries, setImportPreviewEntries] = useState(100);
    const [importTotals, setImportTotals] = useState({ totalRecords: 0, willImport: 0, willSkip: 0, truncated: false });
    const [importResult, setImportResult] = useState(null);
    const [importedFileName, setImportedFileName] = useState('');

    // Recent-import indicators (persist across reloads via sessionStorage)
    const [recentImport, setRecentImport] = useState(() => {
        try {
            const raw = sessionStorage.getItem('tracker_recent_import');
            if (!raw) return { newPrescriberNames: [], updatedPrescriberNames: [], newPatientIds: [], updatedPatientIds: [] };
            const p = JSON.parse(raw);
            return {
                newPrescriberNames: Array.isArray(p.newPrescriberNames) ? p.newPrescriberNames : [],
                updatedPrescriberNames: Array.isArray(p.updatedPrescriberNames) ? p.updatedPrescriberNames : [],
                newPatientIds: Array.isArray(p.newPatientIds) ? p.newPatientIds : [],
                updatedPatientIds: Array.isArray(p.updatedPatientIds) ? p.updatedPatientIds : []
            };
        } catch { return { newPrescriberNames: [], updatedPrescriberNames: [], newPatientIds: [], updatedPatientIds: [] }; }
    });
    const newDocSet = useMemo(() => new Set(recentImport.newPrescriberNames.map(s => (s || '').trim().toUpperCase())), [recentImport.newPrescriberNames]);
    const updatedDocSet = useMemo(() => new Set(recentImport.updatedPrescriberNames.map(s => (s || '').trim().toUpperCase())), [recentImport.updatedPrescriberNames]);
    const newPatientSet = useMemo(() => new Set(recentImport.newPatientIds.map(Number)), [recentImport.newPatientIds]);
    const updatedPatientSet = useMemo(() => new Set(recentImport.updatedPatientIds.map(Number)), [recentImport.updatedPatientIds]);
    const hasIndicators = recentImport.newPrescriberNames.length + recentImport.updatedPrescriberNames.length > 0;
    const clearImportIndicators = () => {
        sessionStorage.removeItem('tracker_recent_import');
        setRecentImport({ newPrescriberNames: [], updatedPrescriberNames: [], newPatientIds: [], updatedPatientIds: [] });
    };

    const STATUS_OPTIONS = [
        'Pending', 'Call Back', 'No Answer', 'Voicemail', 'Follow Up',
        'Processing', 'Fax Sent', 'Email Sent', 'Appointment First',
        'Patient Call', 'Request Electronically', 'Data Not Found',
        'Additional Info', "Don't Need", 'Discontinued', 'Invalid',
        'Completed', 'Others'
    ];

    // Severity mapping for Status Sorting Priority Rule (High to Low severity)
    const STATUS_SEVERITY = {
        'call back': 5,
        'patient call': 4,
        'appointment first': 3,
        'processing': 2,
        'pending': 1
    };

    // Calculate maximum severity among a prescriber's underlying prescriptions
    const getPrescriberSeverity = (prescriber) => {
        let maxSeverity = 0;
        if (prescriber.patients) {
            prescriber.patients.forEach(pat => {
                if (pat.prescriptions) {
                    pat.prescriptions.forEach(rx => {
                        const status = (rx.call_status || 'Pending').toLowerCase().trim();
                        const score = STATUS_SEVERITY[status] || 0;
                        if (score > maxSeverity) {
                            maxSeverity = score;
                        }
                    });
                }
            });
        }
        return maxSeverity;
    };

    useEffect(() => {
        sessionStorage.setItem('tracker_search', searchTerm);
        sessionStorage.setItem('tracker_entries', entriesPerPage);
        sessionStorage.setItem('tracker_page', currentPage);
        sessionStorage.setItem('tracker_status_filter', statusFilter);
        sessionStorage.setItem('tracker_rx_status_filter', rxStatusFilter);
    }, [searchTerm, entriesPerPage, currentPage, statusFilter, rxStatusFilter]);

    const fetchTrackerData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}?action=get_tracker_list&status=${statusFilter}`);
            if (Array.isArray(response.data)) setData(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error:", error);
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchTrackerData();
    }, [fetchTrackerData]);

    // Panel Auto-Focus and scrolling to highlight filtered prescription matches inside Drawer
    useEffect(() => {
        if (drawerOpen && selectedPresc && statusFilter !== 'ALL') {
            let targetId = null;
            let rxId = null;
            // Scan the nested structure to find the first matching prescription
            for (const patient of selectedPresc.patients) {
                if (patient.prescriptions) {
                    for (const rx of patient.prescriptions) {
                        if (rx.call_status.toUpperCase() === statusFilter.toUpperCase()) {
                            targetId = `rx-card-${rx.rx_id}`;
                            rxId = rx.rx_id;
                            break;
                        }
                    }
                }
                if (targetId) break;
            }

            if (targetId && rxId) {
                setHighlightedRxId(rxId);
                setTimeout(() => {
                    const element = document.getElementById(targetId);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 350); // Delay slightly to allow drawer animation/render to finish

                // Remove highlight after 3 seconds
                const timer = setTimeout(() => {
                    setHighlightedRxId(null);
                }, 3000);
                return () => clearTimeout(timer);
            }
        } else {
            setHighlightedRxId(null);
        }
    }, [drawerOpen, selectedPresc, statusFilter]);

    // Helpers
    const formatID = (id) => String(id).padStart(4, '0');

    // Case-insensitive status normalization to match React STATUS_OPTIONS exactly
    const normalizeStatus = (status) => {
        if (!status) return 'Pending';
        const match = STATUS_OPTIONS.find(opt => opt.toLowerCase() === status.toLowerCase().trim());
        return match || 'Pending';
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString.replace(' ', 'T'));
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
            ' ' +
            date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const formatSearchableDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const matchesRxStatusFilter = useCallback((rowStatus) => {
        if (rxStatusFilter === 'ALL') return true;
        const s = (rowStatus || '').toUpperCase();
        if (rxStatusFilter === 'COMPLETED_CALL') {
            return !!rowStatus && !s.includes('PENDING');
        }
        if (rxStatusFilter === 'NOT_COMPLETE') {
            return !rowStatus || s.includes('PENDING');
        }
        return true;
    }, [rxStatusFilter]);

    const matchesStatusFilter = useCallback((rowStatus) => {
        if (statusFilter === 'ALL') return true;
        const s = (rowStatus || '').toUpperCase();
        switch (statusFilter) {
            case 'PENDING':    return !rowStatus || s.includes('PENDING');
            case 'NO ANSWER':  return s.includes('NO ANSWER');
            case 'FOLLOW UP':  return s.includes('FOLLOW UP');
            case 'VOICEMAIL':  return s.includes('VOICEMAIL');
            case 'PROCESSING': return s.includes('PROCESS');
            case 'FAX SENT':   return s.includes('FAX');
            case 'EMAIL SENT': return s.includes('EMAIL');
            case 'COMPLETED':  return s.includes('COMPLETED');
            case 'APPOINTMENT FIRST':      return s.includes('APPOINTMENT');
            case 'CALL BACK':              return s === 'CALL BACK' || s.includes('CALL BACK');
            case 'PATIENT CALL':           return s.includes('PATIENT CALL');
            case 'REQUEST ELECTRONICALLY': return s.includes('REQUEST ELECTRONICALLY') || s.includes('ELECTRONIC');
            case 'DATA NOT FOUND':         return s.includes('DATA NOT FOUND');
            case 'ADDITIONAL INFO':        return s.includes('ADDITIONAL INFO');
            case "DON'T NEED":             return s.includes("DON'T NEED");
            case 'INVALID':                return s === 'INVALID' || s.includes('INVALID');
            case 'DISCONTINUED':           return s.includes('DISCONTINUED');
            case 'OTHERS':                 return s.includes('OTHERS');
            default: return true;
        }
    }, [statusFilter]);

    // Hierarchical frontend search filter logic across prescriber names, prescriber phone, patients, and medications/RXs
    const filteredData = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return data;

        return data.filter(presc => {
            const matchesPresc = (presc.prescriber_name || '').toLowerCase().includes(term) ||
                                 (presc.prescriber_phone || '').toLowerCase().includes(term);
            if (matchesPresc) return true;

            // Check if any patient or underlying prescription details match search query
            const matchesPatient = presc.patients && presc.patients.some(pat => {
                const matchesPatInfo = (pat.patient_name || '').toLowerCase().includes(term) ||
                                       (pat.dob || '').toLowerCase().includes(term);
                if (matchesPatInfo) return true;

                return pat.prescriptions && pat.prescriptions.some(rx => {
                    return (rx.medication_name || '').toLowerCase().includes(term) ||
                           (rx.rx_no || '').toLowerCase().includes(term);
                });
            });

            return matchesPatient;
        });
    }, [data, searchTerm]);

    // Apply front-end Rx Status Filters on top of search results
    const finalFilteredData = useMemo(() => {
        let result = filteredData;
        if (rxStatusFilter === 'ALL') return result;

        return result.filter(presc => {
            if (statusFilter === 'ALL') {
                // Default: check if entire doctor has any pending items
                return rxStatusFilter === 'COMPLETED_CALL' 
                    ? presc.rx_status === 1 
                    : presc.rx_status === 0;
            }

            // If a status filter is active, check completion status of only matching prescriptions
            const matchingRxs = [];
            presc.patients.forEach(pat => {
                if (pat.prescriptions) {
                    pat.prescriptions.forEach(rx => {
                        if (matchesStatusFilter(rx.call_status)) {
                            matchingRxs.push(rx);
                        }
                    });
                }
            });

            // Consider the matched prescriptions completed if none are 'Pending'
            const allMatchingCompleted = matchingRxs.length > 0 && matchingRxs.every(
                rx => (rx.call_status || '').toLowerCase().trim() !== 'pending'
            );

            return rxStatusFilter === 'COMPLETED_CALL' ? allMatchingCompleted : !allMatchingCompleted;
        });
    }, [filteredData, rxStatusFilter, statusFilter, matchesStatusFilter]);

    // Multi-column sorting supporting alphabetical, date, pending counter, and severity urgency sorting rules
    const sortedData = useMemo(() => {
        const sorted = [...finalFilteredData];
        sorted.sort((a, b) => {
            let valA, valB;
            if (sortField === 'name') {
                valA = a.prescriber_name || '';
                valB = b.prescriber_name || '';
                return sortDirection === 'asc' 
                    ? valA.localeCompare(valB) 
                    : valB.localeCompare(valA);
            } else if (sortField === 'date') {
                const parseDate = (d) => d && d !== '0000-00-00 00:00:00' && d !== '0000-00-00' ? new Date(d.replace(' ', 'T')).getTime() : 0;
                valA = parseDate(a.latest_call_date);
                valB = parseDate(b.latest_call_date);
                return sortDirection === 'asc' ? valA - valB : valB - valA;
            } else if (sortField === 'pending') {
                valA = a.pending_calls_count || 0;
                valB = b.pending_calls_count || 0;
                return sortDirection === 'asc' ? valA - valB : valB - valA;
            } else if (sortField === 'status') {
                // Table Status Sorting Priority Rule (Severity hierarchy)
                const severityA = getPrescriberSeverity(a);
                const severityB = getPrescriberSeverity(b);
                return sortDirection === 'asc' ? severityA - severityB : severityB - severityA;
            }
            return 0;
        });
        return sorted;
    }, [finalFilteredData, sortField, sortDirection]);

    const totalEntries = sortedData.length;
    const totalPages = entriesPerPage === 'all' ? 1 : Math.ceil(totalEntries / entriesPerPage);

    const startIndex = entriesPerPage === 'all' ? 0 : (currentPage - 1) * entriesPerPage;
    const endIndex = entriesPerPage === 'all' ? totalEntries : startIndex + entriesPerPage;
    const paginatedData = sortedData.slice(startIndex, endIndex);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [searchTerm, statusFilter, rxStatusFilter, totalPages, currentPage]);

    const handleDownloadFiltered = async () => {
        setDownloadingFiltered(true);
        try {
            const response = await axios.get(`${API_URL}?action=get_tracker_export_data`);
            const fullHistory = response.data;
            console.log("DEBUG EXPORT DATA:", fullHistory);

            if (!Array.isArray(fullHistory)) {
                alert("No data available.");
                setDownloadingFiltered(false);
                return;
            }

            const term = searchTerm.toLowerCase().trim();

            const matchesSearchTerm = (item) => {
                if (!term) return true;
                const rawValues = Object.values(item).map(val => String(val || '').toLowerCase());
                if (rawValues.some(v => v.includes(term))) return true;

                const dateRaw = item.date_logged || item['Date Logged'] || item.latest_call_date || item.timestamp;
                if (dateRaw && dateRaw !== '0000-00-00') {
                    const dateObj = new Date(dateRaw);
                    if (!isNaN(dateObj.getTime())) {
                        const formattedString = dateObj.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        }).toLowerCase();
                        if (formattedString.includes(term)) return true;
                    }
                }
                return false;
            };

            const filteredExport = fullHistory.filter(item =>
                matchesStatusFilter(item.call_status) && matchesRxStatusFilter(item.call_status) && matchesSearchTerm(item)
            );

            if (filteredExport.length === 0) {
                let msg = "No records found";
                if (statusFilter !== 'ALL' && term) {
                    msg = `No records found for status "${statusFilter}" matching "${searchTerm}".`;
                } else if (statusFilter !== 'ALL') {
                    msg = `No records found for status: ${statusFilter}`;
                } else if (term) {
                    msg = `No records found matching your search: ${searchTerm}`;
                }
                alert(msg);
                setDownloadingFiltered(false);
                return;
            }

            generateGroupedExcel(filteredExport);

        } catch (error) {
            console.error("Export Error:", error);
            alert("Failed to download report.");
        } finally {
            setDownloadingFiltered(false);
        }
    };

    const generateGroupedExcel = (rows) => {
        // Group by prescriber
        const groups = new Map();
        rows.forEach(item => {
            const key = (item.prescriber_name || '__unknown__').trim().toUpperCase();
            if (!groups.has(key)) {
                groups.set(key, {
                    prescriber_name: item.prescriber_name || '',
                    prescriber_phone: item.prescriber_phone || '',
                    patients: []
                });
            }
            groups.get(key).patients.push(item);
        });

        const sortedGroups = Array.from(groups.values()).sort((a, b) =>
            (a.prescriber_name || '').localeCompare(b.prescriber_name || '', undefined, { sensitivity: 'base' })
        );

        const header = [
            'Doctor Name', 'Doctor Phone',
            'Patient Name', 'Patient Phone', 'DOB', 'Drug Name', 'RX No.',
            'Call Status', 'Latest Remark'
        ];

        const aoa = [header];

        sortedGroups.forEach(g => {
            // Doctor row
            aoa.push([g.prescriber_name, g.prescriber_phone, '', '', '', '', '', '', '']);
            // Patient sub-rows
            const sortedPatients = [...g.patients].sort((a, b) => {
                const nameCmp = (a.patient_name || '').localeCompare(b.patient_name || '', undefined, { sensitivity: 'base' });
                if (nameCmp !== 0) return nameCmp;
                return String(a.rx_no || '').localeCompare(String(b.rx_no || ''));
            });
            sortedPatients.forEach(p => {
                aoa.push([
                    '', '',
                    p.patient_name || '',
                    p.patient_phone || '',
                    p.dob || '',
                    p.drug_name || '',
                    p.rx_no || '',
                    p.call_status || '',
                    p.remarks_history || ''
                ]);
            });
        });

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [
            { wch: 28 }, { wch: 18 },
            { wch: 28 }, { wch: 18 }, { wch: 14 }, { wch: 24 }, { wch: 14 },
            { wch: 18 }, { wch: 40 }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Tracker');

        const now = new Date();
        const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
        // Build a friendly file name based on the active status filter
        const toTitleCase = (s) => s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        const statusToken = (!statusFilter || statusFilter === 'ALL')
            ? 'ALL'
            : toTitleCase(statusFilter).replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        XLSX.writeFile(wb, `Tracker_Filtered_${statusToken}_${stamp}.xlsx`);
    };

    const handleDownloadAll = async () => {
        setDownloadingAll(true);
        try {
            const response = await axios.get(`${API_URL}?action=get_tracker_export_data`);
            const fullHistory = response.data;

            if (!Array.isArray(fullHistory) || fullHistory.length === 0) {
                alert("No data available in the database.");
                setDownloadingAll(false);
                return;
            }

            // Directly download everything
            generateExcel(fullHistory, "Tracker_FULL_History");

        } catch (error) { console.error(error); alert("Failed to download."); }
        finally { setDownloadingAll(false); }
    };

    // ===== Excel Importer Handlers =====
    const resetImportState = () => {
        setImportStep(1);
        setImportLoading(false);
        setImportError('');
        setImportTempId('');
        setImportPreview([]);
        setImportPreviewPage(1);
        setImportPreviewEntries(100);
        setImportTotals({ totalRecords: 0, willImport: 0, willSkip: 0, truncated: false });
        setImportResult(null);
        setImportedFileName('');
    };

    const openImportModal = () => {
        resetImportState();
        setShowImportModal(true);
    };

    const closeImportModal = () => {
        setShowImportModal(false);
        resetImportState();
    };

    const handleImportUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        event.target.value = null;

        setImportedFileName(file.name);
        setImportLoading(true);
        setImportError('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${NODE_API_URL}/tracker/import/parse`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.success) {
                setImportTempId(res.data.tempId);
                setImportPreview(res.data.preview || []);
                setImportTotals({
                    totalRecords: res.data.totalRecords || 0,
                    willImport: res.data.willImport || 0,
                    willSkip: res.data.willSkip || 0,
                    truncated: !!res.data.truncated
                });
                setImportStep(2);
            } else {
                setImportError(res.data.error || 'Failed to parse file.');
            }
        } catch (err) {
            setImportError(err.response?.data?.error || err.message || 'Upload failed.');
        } finally {
            setImportLoading(false);
        }
    };

    const updatePreviewRow = (index, patch) => {
        setImportPreview(prev => prev.map((r, i) => i === index ? { ...r, ...patch } : r));
    };

    const handleConfirmImport = async () => {
        setImportLoading(true);
        setImportError('');
        try {
            let addedBy = 'System';
            try {
                const u = JSON.parse(localStorage.getItem('user') || '{}');
                addedBy = u.full_name || u.name || u.username || 'System';
            } catch (e) { /* ignore */ }

            // Only send user overrides (skip/status changes) to keep payload small
            // The backend reads the full dataset from its temp JSON file
            const overrides = importPreview
                .map((r, i) => (r.skip || r.status) ? { index: i, skip: !!r.skip, status: r.status } : null)
                .filter(Boolean);

            const res = await axios.post(`${NODE_API_URL}/tracker/import/commit`, {
                tempId: importTempId,
                rows: overrides,
                addedBy
            });
            if (res.data.success) {
                setImportResult(res.data.result);
                setImportStep(3);

                // Merge indicators with any existing ones so consecutive imports accumulate
                const r = res.data.result || {};
                const newDocs = (r.newPrescriberNames || []).map(s => (s || '').trim().toUpperCase());
                const updDocs = (r.updatedPrescriberNames || []).map(s => (s || '').trim().toUpperCase());
                const newPats = (r.newPatientIds || []).map(Number);
                const updPats = (r.updatedPatientIds || []).map(Number);
                const merged = {
                    newPrescriberNames: Array.from(new Set([...recentImport.newPrescriberNames, ...newDocs])),
                    updatedPrescriberNames: Array.from(new Set([...recentImport.updatedPrescriberNames, ...updDocs]))
                        .filter(n => !newDocs.includes(n)),
                    newPatientIds: Array.from(new Set([...recentImport.newPatientIds, ...newPats])),
                    updatedPatientIds: Array.from(new Set([...recentImport.updatedPatientIds, ...updPats]))
                        .filter(id => !newPats.includes(id))
                };
                sessionStorage.setItem('tracker_recent_import', JSON.stringify(merged));
                setRecentImport(merged);

                fetchTrackerData();
            } else {
                setImportError(res.data.error || 'Import failed.');
            }
        } catch (err) {
            setImportError(err.response?.data?.error || err.message || 'Import failed.');
        } finally {
            setImportLoading(false);
        }
    };

    const generateExcel = (data, fileNamePrefix) => {
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data);

        const wscols = [{ wch: 20 }, { wch: 15 }, { wch: 35 }, { wch: 20 }, { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 30 }];
        ws['!cols'] = wscols;

        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const fileName = `${fileNamePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const getStatusBadge = (status) => {
        const s = (status || 'PENDING').toUpperCase();
        if (s.includes('NO ANSWER')) return 'bg-red-100 text-red-700';
        if (s.includes('FOLLOW UP')) return 'bg-yellow-100 text-yellow-700';
        if (s.includes('VOICEMAIL')) return 'bg-purple-100 text-purple-700';
        if (s.includes('EMAIL') || s.includes('FAX')) return 'bg-teal-100 text-teal-700';
        if (s.includes('APPOINTMENT')) return 'bg-sky-100 text-sky-700';
        if (s.includes('CALL BACK')) return 'bg-purple-100 text-purple-700';
        if (s.includes('PATIENT CALL')) return 'bg-orange-100 text-orange-700';
        if (s.includes('REQUEST ELECTRONICALLY') || s.includes('ELECTRONIC')) return 'bg-pink-100 text-pink-700';
        if (s.includes('DATA NOT FOUND')) return 'bg-yellow-100 text-yellow-700';
        if (s.includes('ADDITIONAL INFO')) return 'bg-blue-100 text-blue-700';
        if (s.includes("DON'T NEED")) return 'bg-gray-200 text-gray-700';
        if (s.includes('DISCONTINUED')) return 'bg-red-800 text-white';
        if (s === 'INVALID' || s.includes('INVALID')) return 'bg-red-500 text-white';
        if (s.includes('PROCESSING') || s.includes('PROCESS')) return 'bg-green-100 text-green-700';
        if (s.includes('COMPLETED')) return 'bg-green-100 text-green-700';
        if (s === 'PENDING') return 'bg-yellow-100 text-yellow-800';
        return 'bg-gray-100 text-gray-600';
    };

    const executeDelete = async () => {
        if (!itemToDelete) return;
        try { await axios.post(`${API_URL}?action=delete_prescription`, { id: itemToDelete.id }); fetchTrackerData(); setShowDeleteModal(false); } catch (error) { alert("Error deleting"); }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}?action=update_prescriber_info`, editForm);
            alert("Updated Successfully!");
            setShowEditModal(false);
            fetchTrackerData();
        } catch (error) { console.error(error); alert("Error updating"); }
    };

    // Toggle sorting columns
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Initialize drawer form edits when a prescriber is selected
    useEffect(() => {
        if (selectedPresc) {
            const initialEdits = {};
            selectedPresc.patients.forEach(pat => {
                pat.prescriptions.forEach(rx => {
                    initialEdits[rx.rx_id] = {
                        rx_id: rx.rx_id,
                        rx_no: rx.rx_no,
                        call_status: rx.call_status,
                        remarks: rx.remarks
                    };
                });
            });
            setDrawerEdits(initialEdits);
        }
    }, [selectedPresc]);

    // Handle dropdown status change in drawer
    const handleCallStatusChange = (rx_id, newStatus) => {
        setDrawerEdits(prev => ({
            ...prev,
            [rx_id]: {
                ...prev[rx_id],
                call_status: newStatus
            }
        }));
    };

    // Handle Rx Status checkbox toggling in drawer
    const handleRxStatusToggle = (rx_id, checked) => {
        const newStatus = checked ? 'Completed' : 'Pending';
        setDrawerEdits(prev => ({
            ...prev,
            [rx_id]: {
                ...prev[rx_id],
                call_status: newStatus
            }
        }));
    };

    // Handle remarks textarea editing in drawer
    const handleRemarksChange = (rx_id, text) => {
        setDrawerEdits(prev => ({
            ...prev,
            [rx_id]: {
                ...prev[rx_id],
                remarks: text
            }
        }));
    };

    // Save changes to database and refresh dashboard
    const handleSaveChanges = async () => {
        if (!selectedPresc) return;
        setDrawerSaving(true);
        try {
            let agent = 'System';
            try {
                const u = JSON.parse(localStorage.getItem('user') || '{}');
                agent = u.full_name || u.name || u.username || 'System';
            } catch (e) {}

            const response = await axios.post(`${API_URL}?action=save_tracker_changes`, {
                prescriber_name: selectedPresc.prescriber_name,
                updates: Object.values(drawerEdits),
                added_by: agent
            });

            if (response.data.success) {
                alert("Changes saved successfully!");
                setDrawerOpen(false);
                fetchTrackerData(); // Refresh the main dashboard
            } else {
                alert("Failed to save changes: " + (response.data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error("Save error:", error);
            alert("An error occurred while saving.");
        } finally {
            setDrawerSaving(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    return (
        <div className="relative">
            {/* HEADER CARD */}
            <div className="relative overflow-hidden bg-gradient-to-r from-green-800 to-teal-600 rounded-2xl shadow-lg mb-6 p-6">
                <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 30%, white 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
                <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 shadow-md">
                            <Activity className="text-white" size={28} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white tracking-tight">Prescription Call Tracker</h2>
                            <p className="text-xs text-green-100/90 mt-0.5">Track calls, responses, and log call statuses with prescribers &amp; patients</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
                        <div className="text-right">
                            <p className="text-[10px] uppercase tracking-wider text-green-100/80 font-semibold">Total Prescribers</p>
                            <p className="text-xl font-bold text-white leading-none">{sortedData.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CONTROLS BAR */}
            <div className="mb-4">
                <div className="flex flex-wrap gap-2 items-center justify-between">
                <div className="flex flex-wrap gap-2 items-center">
                    {/* SEARCH BAR */}
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search prescribers, patients, RX..."
                            className="pl-10 pr-4 py-2 bg-white border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 transition w-72 text-sm shadow-sm"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>

                    {/* STATUS FILTER */}
                    <div className="relative">
                        <Filter className="absolute left-3 top-2.5 text-gray-400 pointer-events-none" size={16} />
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            className="pl-9 pr-8 py-2 bg-white border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm appearance-none cursor-pointer transition shadow-sm"
                            title="Filter by latest call status"
                        >
                            <option value="ALL">All Statuses</option>
                            <option value="PENDING">Pending</option>
                            <option value="APPOINTMENT FIRST">Appointment First</option>
                            <option value="CALL BACK">Call Back</option>
                            <option value="PATIENT CALL">Patient Call</option>
                            <option value="PROCESSING">Processing</option>
                            <option value="REQUEST ELECTRONICALLY">Request Electronically</option>
                            <option value="DATA NOT FOUND">Data Not Found</option>
                            <option value="ADDITIONAL INFO">Additional Info</option>
                            <option value="DON'T NEED">Don't Need</option>
                            <option value="INVALID">Invalid</option>
                            <option value="DISCONTINUED">Discontinued</option>
                            <option value="OTHERS">Others</option>
                        </select>
                        <ChevronDown className="absolute right-2.5 top-2.5 text-gray-400 pointer-events-none" size={16} />
                    </div>

                    {/* RX STATUS FILTER */}
                    <div className="relative">
                        <Filter className="absolute left-3 top-2.5 text-gray-400 pointer-events-none" size={16} />
                        <select
                            value={rxStatusFilter}
                            onChange={(e) => { setRxStatusFilter(e.target.value); setCurrentPage(1); }}
                            className="pl-9 pr-8 py-2 bg-white border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm appearance-none cursor-pointer transition shadow-sm"
                            title="Filter by Rx Status"
                        >
                            <option value="ALL">All Rx Status</option>
                            <option value="COMPLETED_CALL">Completed Call</option>
                            <option value="NOT_COMPLETE">Not Complete</option>
                        </select>
                        <ChevronDown className="absolute right-2.5 top-2.5 text-gray-400 pointer-events-none" size={16} />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                    {/* ✅ BUTTON 1: EXPORT FILTERED */}
                    <button
                        onClick={handleDownloadFiltered}
                        disabled={downloadingFiltered || downloadingAll}
                        className={`
                    px-4 py-2 rounded-full flex items-center gap-2 shadow-sm text-white text-sm font-semibold transition-all whitespace-nowrap
                    ${downloadingFiltered ? 'bg-teal-400 cursor-wait' : 'bg-teal-600 hover:bg-teal-700 hover:shadow-md'}
                `}
                        title="Download only matching search results"
                    >
                        {downloadingFiltered ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
                        {downloadingFiltered ? "Filtering..." : "Export Filtered"}
                    </button>

                    {/* ✅ BUTTON 1.5: IMPORT EXCEL */}
                    <button
                        onClick={openImportModal}
                        className="px-4 py-2 rounded-full flex items-center gap-2 shadow-sm text-white text-sm font-semibold transition-all whitespace-nowrap bg-emerald-500 hover:bg-emerald-600 hover:shadow-md"
                        title="Import the A & G Refill Request Excel file"
                    >
                        <Upload size={14} />
                        Import Excel
                    </button>

                    {/* ✅ BUTTON 2: DOWNLOAD ALL */}
                    <button
                        onClick={handleDownloadAll}
                        disabled={downloadingFiltered || downloadingAll}
                        className={`
                    px-4 py-2 rounded-full flex items-center gap-2 shadow-sm text-white text-sm font-semibold transition-all whitespace-nowrap
                    ${downloadingAll ? 'bg-indigo-400 cursor-wait' : 'bg-indigo-500 hover:bg-indigo-600 hover:shadow-md'}
                `}
                        title="Download entire database history"
                    >
                        {downloadingAll ? <Loader2 className="animate-spin" size={14} /> : <FileSpreadsheet size={14} />}
                        {downloadingAll ? "Loading All..." : "Download All"}
                    </button>
                </div>
                </div>
            </div>

            {hasIndicators && (
                <div className="mb-3 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <div className="text-sm text-emerald-800 flex items-center gap-2 flex-wrap">
                        <Sparkles size={16} className="text-emerald-600" />
                        <span>
                            <strong>{recentImport.newPrescriberNames.length}</strong> new and{' '}
                            <strong>{recentImport.updatedPrescriberNames.length}</strong> updated prescriber(s) from the most recent Excel import.
                        </span>
                    </div>
                    <button
                        onClick={clearImportIndicators}
                        className="text-xs text-emerald-700 hover:text-emerald-900 underline"
                    >
                        Clear indicators
                    </button>
                </div>
            )}

            <div className="flex justify-between items-center mb-4 px-1">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-teal-900 font-medium">Show</span>
                    <select
                        value={entriesPerPage}
                        onChange={(e) => {
                            setEntriesPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                            setCurrentPage(1);
                        }}
                        className="border-none shadow-sm rounded-md bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 font-medium text-teal-900"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value="all">All</option>
                    </select>
                    <span className="text-sm text-teal-900 font-medium">entries</span>
                </div>

                <div className="text-sm text-teal-900 font-medium">
                    Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
                </div>
            </div>

            <div className="bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-300 mb-6">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-teal-900 divide-y divide-gray-300 border-collapse">
                        <thead className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-300 uppercase text-teal-900 font-bold">
                            <tr>
                                <th 
                                    onClick={() => handleSort('date')}
                                    className="px-4 py-3.5 tracking-wider text-[11px] font-bold border-r border-gray-300 last:border-r-0 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                                >
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        Date & Time
                                        {sortField === 'date' && (
                                            <span className="text-[9px] bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                                                {sortDirection === 'asc' ? 'Oldest to Newest ▲' : 'Newest to Oldest ▼'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th className="px-4 py-3.5 tracking-wider text-[11px] font-bold border-r border-gray-300 last:border-r-0 select-none">
                                    Patient Info
                                </th>
                                <th 
                                    onClick={() => handleSort('name')}
                                    className="px-4 py-3.5 tracking-wider text-[11px] font-bold border-r border-gray-300 last:border-r-0 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                                >
                                    <div className="flex items-center gap-1.5">
                                        Prescriber Contact
                                        {sortField === 'name' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                                    </div>
                                </th>
                                <th className="px-4 py-3.5 tracking-wider text-[11px] font-bold border-r border-gray-300 last:border-r-0 select-none">
                                    Prescriber Phone
                                </th>
                                <th 
                                    onClick={() => handleSort('pending')}
                                    className="px-4 py-3.5 text-center tracking-wider text-[11px] font-bold border-r border-gray-300 last:border-r-0 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                                >
                                    <div className="flex items-center justify-center gap-1.5">
                                        Pending Calls
                                        {sortField === 'pending' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
                                    </div>
                                </th>
                                <th className="px-4 py-3.5 text-center tracking-wider text-[11px] font-bold select-none">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-300 bg-white">
                            {loading ? (
                                <tr><td colSpan="6" className="text-center py-12"><div className="flex flex-col items-center gap-2 text-gray-400"><Loader2 className="animate-spin text-teal-500" size={24} /><span className="text-xs">Loading tracker data...</span></div></td></tr>
                            ) : paginatedData.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-16"><div className="flex flex-col items-center gap-2 text-gray-300"><Phone size={32} className="opacity-40" /><span className="italic text-xs">No tracking records found.</span></div></td></tr>
                            ) : paginatedData.map((presc) => {
                                const hasMultiple = presc.patients.length > 1;
                                const firstPatient = presc.patients[0];
                                
                                return (
                                <tr
                                    key={presc.prescriber_name}
                                    onClick={() => {
                                        setSelectedPresc(presc);
                                        setDrawerOpen(true);
                                    }}
                                    className="transition duration-150 cursor-pointer border-b border-gray-200 hover:bg-teal-50/60"
                                >
                                    <td className="px-4 py-3 text-center border-r border-gray-300 last:border-r-0">
                                        {presc.latest_call_date ? (
                                            <span className="font-bold text-gray-800 flex items-center gap-1 justify-center">
                                                {formatDateTime(presc.latest_call_date)}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>

                                    <td className="px-4 py-3 text-center border-r border-gray-300 last:border-r-0">
                                        <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase shadow-sm">
                                            <Users size={11} /> {presc.patients.length} {presc.patients.length === 1 ? 'Patient' : 'Patients'}
                                        </span>
                                    </td>

                                    <td className="px-4 py-3 text-center border-r border-gray-300 last:border-r-0">
                                        <div className="flex items-center gap-2 justify-center">
                                            <span className="font-bold text-teal-600 hover:underline">{presc.prescriber_name}</span>
                                            {newDocSet.has((presc.prescriber_name || '').trim().toUpperCase()) && (
                                                <span className="text-[9px] font-bold uppercase tracking-wide bg-emerald-500 text-white px-1.5 py-0.5 rounded-full shadow-sm" title="New prescriber from recent import">NEW</span>
                                            )}
                                            {!newDocSet.has((presc.prescriber_name || '').trim().toUpperCase()) && updatedDocSet.has((presc.prescriber_name || '').trim().toUpperCase()) && (
                                                <span className="text-[9px] font-bold uppercase tracking-wide bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full shadow-sm" title="Updated by recent import">UPDATED</span>
                                            )}
                                            <ChevronDown size={14} className="text-teal-500 opacity-60" />
                                        </div>
                                    </td>

                                    <td className="px-4 py-3 text-center border-r border-gray-300 last:border-r-0">{presc.prescriber_phone || '-'}</td>

                                    <td className="px-4 py-3 text-center border-r border-gray-300 last:border-r-0">
                                        {presc.pending_calls_count > 0 ? (
                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold shadow-sm">
                                                {presc.pending_calls_count}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-500 text-xs font-bold shadow-sm">
                                                0
                                            </span>
                                        )}
                                    </td>

                                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const firstRx = presc.patients?.[0]?.prescriptions?.[0];
                                                    if (firstRx) {
                                                        setEditForm({
                                                            id: firstRx.id,
                                                            prescriber_name: presc.prescriber_name,
                                                            prescriber_phone: presc.prescriber_phone || '',
                                                            patient_name: presc.patients?.[0]?.patient_name || '',
                                                            patient_phone: presc.patients?.[0]?.patient_phone || '',
                                                            dob: presc.patients?.[0]?.dob || '',
                                                            insurance_provider: presc.patients?.[0]?.insurance_provider || ''
                                                        });
                                                        setShowEditModal(true);
                                                    }
                                                }}
                                                className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white text-teal-700 hover:bg-teal-50 border border-teal-200 text-[11px] font-bold transition shadow-sm"
                                                title="Edit this prescriber's tracker details"
                                            >
                                                <Edit size={13} /> Edit
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const firstRx = presc.patients?.[0]?.prescriptions?.[0];
                                                    if (firstRx) {
                                                        setItemToDelete({ id: firstRx.id, prescriber_name: presc.prescriber_name });
                                                        setShowDeleteModal(true);
                                                    }
                                                }}
                                                className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white text-rose-600 hover:bg-rose-50 border border-rose-200 text-[11px] font-bold transition shadow-sm"
                                                title="Delete this tracker entry"
                                            >
                                                <Trash2 size={13} /> Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {entriesPerPage !== 'all' && totalPages > 1 && (
                <div className="flex flex-wrap justify-center items-center gap-4 mt-6">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm font-medium text-gray-600 transition"
                        >
                            <ChevronLeft size={16} /> Previous
                        </button>

                        <div className="flex gap-1">
                            {[...Array(totalPages)].map((_, idx) => {
                                const pageNum = idx + 1;
                                if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`min-w-[36px] px-3 py-1.5 rounded-lg text-sm font-semibold transition shadow-sm ${currentPage === pageNum ? 'bg-gradient-to-r from-green-800 to-teal-600 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                    return <span key={pageNum} className="px-2 text-gray-400">…</span>;
                                }
                                return null;
                            })}
                        </div>

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm font-medium text-gray-600 transition"
                        >
                            Next <ChevronRight size={16} />
                        </button>
                    </div>

                    <div className="flex items-center gap-2 bg-white border border-gray-200 pl-3 pr-2 py-1.5 rounded-lg shadow-sm">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Jump to:</span>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                min="1"
                                max={totalPages}
                                placeholder="Page"
                                className="w-12 px-1.5 py-0.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-center font-extrabold text-gray-700 bg-gray-50 text-xs"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        const val = parseInt(e.target.value, 10);
                                        if (val >= 1 && val <= totalPages) {
                                            setCurrentPage(val);
                                            e.target.value = '';
                                        } else {
                                            alert(`Please enter a page between 1 and ${totalPages}`);
                                        }
                                    }
                                }}
                            />
                            <span className="text-xs text-gray-400 font-medium">/ {totalPages}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT MODAL */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-[500px] p-6 relative">
                        <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X size={24} /></button>
                        <h3 className="text-xl font-bold mb-4 text-teal-700">Edit Record Details</h3>

                        <form onSubmit={handleUpdateSubmit} className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prescriber Name</label>
                                    <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.prescriber_name} onChange={e => setEditForm({ ...editForm, prescriber_name: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prescriber Phone</label>
                                    <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.prescriber_phone} onChange={e => setEditForm({ ...editForm, prescriber_phone: e.target.value })} />
                                </div>
                            </div>

                            <hr className="border-gray-100 my-2" />

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient Name</label>
                                    <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.patient_name} onChange={e => setEditForm({ ...editForm, patient_name: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Patient Phone</label>
                                    <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.patient_phone} onChange={e => setEditForm({ ...editForm, patient_phone: e.target.value })} />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">DOB</label>
                                    <input type="date" className="w-full border p-2 rounded text-sm" value={editForm.dob} onChange={e => setEditForm({ ...editForm, dob: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Insurance Code</label>
                                    <input type="text" className="w-full border p-2 rounded text-sm" value={editForm.insurance_provider} onChange={e => setEditForm({ ...editForm, insurance_provider: e.target.value })} />
                                </div>
                            </div>

                            <button type="submit" className="w-full bg-teal-600 text-white font-bold py-2 rounded hover:bg-teal-700 flex items-center justify-center gap-2 mt-4">
                                <Save size={16} /> Update Details
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && itemToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-96 p-6 relative text-center">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                            <AlertTriangle className="h-6 w-6 text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
                        <p className="text-sm text-gray-500 mt-2">
                            Are you sure you want to remove this tracker entry for <strong>{itemToDelete.prescriber_name}</strong>?
                        </p>
                        <div className="mt-6 flex justify-center gap-3">
                            <button onClick={() => setShowDeleteModal(false)} className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded font-medium hover:bg-gray-50">Cancel</button>
                            <button onClick={executeDelete} className="bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700">Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Excel Importer Modal ===== */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col relative">
                        <button
                            onClick={closeImportModal}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 z-10"
                            title="Close"
                        >
                            <X size={24} />
                        </button>

                        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                            <div className="flex justify-center gap-10 mb-1">
                                {[1, 2, 3].map(s => (
                                    <div key={s} className="flex flex-col items-center">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all duration-300 ${
                                            importStep > s
                                                ? 'bg-emerald-500 border-emerald-500 text-white'
                                                : importStep === s
                                                    ? 'bg-emerald-500 border-emerald-500 text-white shadow-md shadow-emerald-200'
                                                    : 'bg-white border-gray-300 text-gray-400'
                                        }`}>
                                            {importStep > s ? '✓' : s}
                                        </div>
                                        <span className={`text-xs mt-2 font-medium transition-colors ${importStep >= s ? 'text-gray-800' : 'text-gray-400'}`}>
                                            {s === 1 ? 'Upload' : s === 2 ? 'Preview' : 'Complete'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="px-6 py-5 overflow-y-auto flex-1">
                            {importError && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2 items-start">
                                    <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                                    <span className="text-sm text-red-700">{importError}</span>
                                </div>
                            )}

                            {importStep === 1 && (
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
                                        <Upload className="text-emerald-600" size={22} /> Upload Refill Request Excel
                                    </h3>
                                    <p className="text-sm text-gray-500 mb-5 ml-8">
                                        Upload the <strong>A & G Pharmacy Refill Request</strong> spreadsheet (.xlsx/.xls/.csv). The system will fully read all rows, extract medication, DOB, and tracker statuses, and allow you to review before importing. Missing patients and prescriptions will be automatically created.
                                    </p>

                                    <div className="border-2 border-dashed border-emerald-300 bg-emerald-50/30 rounded-xl p-12 text-center hover:border-emerald-500 hover:bg-emerald-50/60 transition-all duration-200 cursor-pointer relative group">
                                        <input
                                            type="file"
                                            accept=".xlsx,.xls,.csv"
                                            onChange={handleImportUpload}
                                            disabled={importLoading}
                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                        />
                                        <FileText className="mx-auto text-emerald-400 mb-3 group-hover:text-emerald-500 transition-colors" size={48} />
                                        <p className="text-sm text-gray-600">
                                            {importedFileName ? <><strong>{importedFileName}</strong> selected</> : (
                                                <>Drag and drop or click to select<br />
                                                    <span className="text-xs text-gray-400">Supports .xlsx, .xls, .csv</span>
                                                </>
                                            )}
                                        </p>
                                    </div>
                                    {importLoading && (
                                        <p className="text-center text-emerald-600 mt-4 flex items-center justify-center gap-2">
                                            <Loader2 className="animate-spin" size={16} /> Parsing file...
                                        </p>
                                    )}
                                    <div className="flex justify-end mt-5">
                                        <button
                                            onClick={closeImportModal}
                                            className="px-5 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {importStep === 2 && (
                                <div>
                                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                            <FileSpreadsheet className="text-emerald-600" /> Review Rows
                                        </h3>
                                        <div className="text-sm flex gap-4 bg-emerald-50 text-emerald-800 px-4 py-2 rounded-lg font-medium border border-emerald-100 shadow-sm">
                                            <span><strong>{importTotals.totalRecords}</strong> total</span>
                                            <span className="text-emerald-700"><strong>{importTotals.totalRecords - importPreview.filter(r => r.skip).length}</strong> will import</span>
                                            <span className="text-red-600"><strong>{importPreview.filter(r => r.skip).length}</strong> will skip</span>
                                        </div>
                                    </div>
                                    {importTotals.truncated && (
                                        <div className="mb-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                                            <AlertCircle className="text-blue-500 flex-shrink-0" size={15} />
                                            <span className="text-xs text-blue-700 font-medium">Showing first 500 of <strong>{importTotals.totalRecords}</strong> rows in preview. All <strong>{importTotals.totalRecords}</strong> rows will be processed on confirm.</span>
                                        </div>
                                    )}

                                    <div className="flex justify-between items-center mb-3 mt-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-600 font-medium">Show</span>
                                            <select
                                                value={importPreviewEntries}
                                                onChange={(e) => {
                                                    setImportPreviewEntries(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                                                    setImportPreviewPage(1);
                                                }}
                                                className="border border-gray-200 shadow-sm rounded-md bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium text-emerald-900 cursor-pointer"
                                            >
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                                <option value={500}>500</option>
                                                <option value="all">All</option>
                                            </select>
                                            <span className="text-sm text-gray-600 font-medium">entries</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setImportPreviewPage(Math.max(1, importPreviewPage - 1))}
                                                disabled={importPreviewPage === 1}
                                                className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50 transition"
                                            ><ChevronLeft size={16} /></button>
                                            <span className="text-xs font-medium content-center px-3 py-1 border rounded bg-gray-50 text-center">
                                                Page {importPreviewPage} of {importPreviewEntries === 'all' ? 1 : Math.max(1, Math.ceil(importPreview.length / importPreviewEntries))}
                                            </span>
                                            <button
                                                onClick={() => setImportPreviewPage(Math.min(Math.max(1, Math.ceil(importPreview.length / importPreviewEntries)), importPreviewPage + 1))}
                                                disabled={importPreviewEntries === 'all' || importPreviewPage >= Math.max(1, Math.ceil(importPreview.length / importPreviewEntries))}
                                                className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50 transition"
                                            ><ChevronRight size={16} /></button>
                                        </div>
                                    </div>

                                    <div className="overflow-auto max-h-[55vh] border border-gray-200 rounded-lg">
                                        <table className="min-w-full text-xs">
                                            <thead className="bg-gray-50 sticky top-0 z-10">
                                                <tr className="text-left text-gray-600 bg-gray-100 border-b border-gray-200">
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Skip</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Date</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Patient</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">DOB</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Doctor</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Doc Phone</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Medication</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">RX No.</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Unify RX</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Status</th>
                                                    <th className="px-3 py-2.5 font-bold uppercase tracking-wider text-[10px]">Remark</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {(() => {
                                                    const totalPreviewEntries = importPreview.length;
                                                    const previewStartIndex = importPreviewEntries === 'all' ? 0 : (importPreviewPage - 1) * importPreviewEntries;
                                                    const previewEndIndex = importPreviewEntries === 'all' ? totalPreviewEntries : previewStartIndex + importPreviewEntries;
                                                    const paginatedPreview = importPreview.slice(previewStartIndex, previewEndIndex);

                                                    return paginatedPreview.map((r, loopIdx) => {
                                                        const i = previewStartIndex + loopIdx;
                                                        const missing = !r.rx_exists;
                                                        return (
                                                            <tr key={i} className={`hover:bg-gray-50 transition-colors ${r.skip ? 'opacity-50 bg-gray-50' : 'bg-white'}`}>
                                                            <td className="px-3 py-2 align-top text-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!r.skip}
                                                                    onChange={(e) => updatePreviewRow(i, { skip: e.target.checked })}
                                                                    className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
                                                                />
                                                            </td>
                                                            <td className="px-3 py-2 align-top whitespace-nowrap text-gray-600 font-medium">{r.log_date || '-'}</td>
                                                            <td className="px-3 py-2 align-top font-bold text-teal-800">{r.patient_name || '-'}</td>
                                                            <td className="px-3 py-2 align-top whitespace-nowrap text-gray-500">{r.dob || '-'}</td>
                                                            <td className="px-3 py-2 align-top font-medium text-gray-700">{r.prescriber_name || '-'}</td>
                                                            <td className="px-3 py-2 align-top whitespace-nowrap text-gray-500">{r.prescriber_phone || '-'}</td>
                                                            <td className="px-3 py-2 align-top text-gray-800 max-w-[150px] truncate" title={r.drug_name}>{r.drug_name || '-'}</td>
                                                            <td className="px-3 py-2 align-top whitespace-nowrap font-medium text-gray-600">{r.rx_no || '-'}</td>
                                                            <td className="px-3 py-2 align-top text-center">
                                                                {r.unify_rx ? <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-[10px] font-bold">YES</span> : <span className="text-gray-400">-</span>}
                                                            </td>
                                                            <td className="px-3 py-2 align-top">
                                                                <select
                                                                    value={r.status || 'Pending'}
                                                                    onChange={(e) => updatePreviewRow(i, { status: e.target.value })}
                                                                    className="border border-gray-200 rounded px-2 py-1 text-xs bg-white text-gray-700 focus:ring-1 focus:ring-emerald-500 shadow-sm"
                                                                >
                                                                    {STATUS_OPTIONS.map(s => (
                                                                        <option key={s} value={s}>{s}</option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td className="px-3 py-2 align-top max-w-[200px] truncate text-gray-600 text-[11px]" title={r.remarks}>{r.remarks || ''}</td>
                                                        </tr>
                                                    );
                                                })
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="flex justify-end gap-2 mt-4">
                                        <button
                                            onClick={() => { setImportStep(1); }}
                                            disabled={importLoading}
                                            className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium"
                                        >
                                            Back
                                        </button>
                                        <button
                                            onClick={handleConfirmImport}
                                            disabled={importLoading || importPreview.filter(r => !r.skip).length === 0}
                                            className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-md text-white text-sm font-bold flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {importLoading ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle size={14} />}
                                            {importLoading ? 'Importing...' : 'Confirm Import'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {importStep === 3 && importResult && (
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <CheckCircle className="text-green-600" /> Import Complete
                                    </h3>
                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                                            <div className="text-2xl font-bold text-green-700">{importResult.imported}</div>
                                            <div className="text-xs text-green-700 uppercase tracking-wide">Imported</div>
                                        </div>
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                                            <div className="text-2xl font-bold text-yellow-700">{importResult.skipped}</div>
                                            <div className="text-xs text-yellow-700 uppercase tracking-wide">Skipped</div>
                                        </div>
                                    </div>
                                    {(importResult.createdPatients?.length > 0 || importResult.createdPrescribers?.length > 0) && (
                                        <p className="text-xs text-gray-600 mb-2">
                                            Created <strong>{importResult.createdPatients?.length || 0}</strong> new patient(s),{' '}
                                            <strong>{importResult.createdPrescribers?.length || 0}</strong> new prescriber(s).
                                        </p>
                                    )}
                                    {importResult.missingRx?.length > 0 && (
                                        <details className="mt-3">
                                            <summary className="text-sm text-red-700 cursor-pointer font-medium">
                                                {importResult.missingRx.length} row(s) skipped — RX not found
                                            </summary>
                                            <div className="max-h-48 overflow-y-auto mt-2 border border-red-100 rounded-lg">
                                                <table className="min-w-full text-xs">
                                                    <thead className="bg-red-50">
                                                        <tr>
                                                            <th className="px-2 py-1.5 text-left font-semibold">Patient</th>
                                                            <th className="px-2 py-1.5 text-left font-semibold">RX No.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {importResult.missingRx.map((m, i) => (
                                                            <tr key={i} className="border-t border-red-100">
                                                                <td className="px-2 py-1">{m.patient_name || '-'}</td>
                                                                <td className="px-2 py-1">{m.rx_no || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </details>
                                    )}
                                    <div className="flex justify-end mt-5">
                                        <button
                                            onClick={closeImportModal}
                                            className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Slide-Out Detail Drawer Panel ===== */}
            {drawerOpen && selectedPresc && (
                <>
                    {/* Backdrop */}
                    <div 
                        className="fixed inset-0 bg-black/45 backdrop-blur-xs z-40 transition-opacity duration-300"
                        onClick={() => setDrawerOpen(false)}
                    />
                    
                    {/* Right Slide-out Drawer Panel */}
                    <div className="fixed top-0 right-0 h-full w-[450px] sm:w-[500px] md:w-[550px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col animate-slide-in">
                        {/* Drawer Header */}
                        <div className="p-4 border-b border-gray-200 bg-teal-50">
                            <button 
                                onClick={() => setDrawerOpen(false)} 
                                className="flex items-center gap-1.5 text-xs font-bold text-teal-800 hover:text-teal-900 uppercase tracking-wider mb-2 focus:outline-none transition-colors"
                            >
                                <X size={16} /> Close Panel
                            </button>
                            <h3 className="text-lg font-extrabold text-teal-950 truncate">
                                Dr. {selectedPresc.prescriber_name} | Prescriber Details
                            </h3>
                            <div className="flex items-center gap-3 text-xs text-teal-850 font-medium mt-1">
                                <span><strong>Total Patients:</strong> {selectedPresc.patients.length}</span>
                                <span className="text-teal-400">|</span>
                                <span><strong>Total Prescriptions:</strong> {selectedPresc.total_rx_count}</span>
                            </div>
                        </div>

                        {/* Drawer Body - Patients List and Medication Details */}
                        <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-4">
                            {selectedPresc.patients.map((pat) => (
                                <div key={pat.patient_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                    {/* Patient Card Title Bar */}
                                    <div className="px-4 py-3 bg-gradient-to-r from-emerald-50/60 to-teal-50/40 border-b border-gray-200 flex items-center justify-between text-teal-900">
                                        <div className="flex items-center gap-2 font-bold text-xs">
                                            <Users size={14} className="text-teal-600" />
                                            <span>{pat.patient_name} <span className="text-teal-600/70 font-medium ml-1">(DOB: {pat.dob ? new Date(pat.dob).toLocaleDateString('en-US') : '-'})</span></span>
                                        </div>
                                        {newPatientSet.has(Number(pat.patient_id)) && (
                                            <span className="text-[8px] font-extrabold uppercase bg-emerald-500 text-white px-1.5 py-0.5 rounded-full shadow-sm">NEW</span>
                                        )}
                                        {!newPatientSet.has(Number(pat.patient_id)) && updatedPatientSet.has(Number(pat.patient_id)) && (
                                            <span className="text-[8px] font-extrabold uppercase bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded-full shadow-sm">UPDATED</span>
                                        )}
                                    </div>

                                    {/* Patient's Prescriptions List */}
                                    <div className="p-3 space-y-3">
                                        {pat.prescriptions && pat.prescriptions.map((rx) => {
                                            const currentEdit = drawerEdits[rx.rx_id] || { call_status: rx.call_status, remarks: rx.remarks };
                                            const isRxStatusChecked = currentEdit.call_status.toLowerCase() !== 'pending';
                                            const isHighlighted = rx.rx_id === highlightedRxId;

                                            return (
                                                <div 
                                                    key={rx.rx_id} 
                                                    id={`rx-card-${rx.rx_id}`}
                                                    className={`p-4 rounded-xl border transition-all duration-300 relative overflow-hidden ${
                                                        isHighlighted 
                                                            ? 'border-teal-400 bg-teal-50/40 shadow-[0_4px_12px_rgba(20,184,166,0.15)] ring-1 ring-teal-400' 
                                                            : 'border-gray-200 bg-white hover:border-teal-200 hover:shadow-sm'
                                                    }`}
                                                >
                                                    {isHighlighted && <div className="absolute top-0 left-0 bottom-0 w-1 bg-teal-500"></div>}
                                                    {/* Prescription Medication details & status dropdown */}
                                                    <div className="flex items-center justify-between gap-2 mb-2">
                                                        <div className="flex items-center gap-1.5 font-bold text-gray-800 text-xs min-w-0 flex-1">
                                                            <Layers size={13} className="text-teal-500 flex-shrink-0" />
                                                            <span className="truncate" title={rx.medication_name}>{rx.medication_name}</span>
                                                        </div>
                                                        
                                                        {/* Status update fields */}
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            {/* Dropdown status */}
                                                            <select
                                                                value={normalizeStatus(currentEdit.call_status)}
                                                                onChange={(e) => handleCallStatusChange(rx.rx_id, e.target.value)}
                                                                className={`px-2 py-1 rounded text-[11px] font-bold border-none outline-none shadow-sm cursor-pointer ${getStatusBadge(currentEdit.call_status)}`}
                                                            >
                                                                {STATUS_OPTIONS.map((opt) => (
                                                                    <option key={opt} value={opt} className="bg-white text-gray-800 font-semibold">{opt}</option>
                                                                ))}
                                                            </select>

                                                            {/* Checkbox Rx Status */}
                                                            <label className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-600 select-none cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isRxStatusChecked}
                                                                    onChange={(e) => handleRxStatusToggle(rx.rx_id, e.target.checked)}
                                                                    className="w-3.5 h-3.5 accent-teal-600 cursor-pointer"
                                                                />
                                                                <span>Rx Status</span>
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {/* Remarks Input Textarea */}
                                                    <div className="space-y-1">
                                                        <span className="block text-[10px] font-bold text-teal-700/80 uppercase tracking-wider mb-1">Remarks / Notes</span>
                                                        <textarea
                                                            value={currentEdit.remarks}
                                                            onChange={(e) => handleRemarksChange(rx.rx_id, e.target.value)}
                                                            placeholder="Add follow-up notes here..."
                                                            rows={2}
                                                            className="w-full text-xs p-2 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition resize-none text-gray-700"
                                                        />
                                                    </div>

                                                    {/* View Tracker Details Button */}
                                                    <div className="flex items-center justify-end mt-4 pt-3 border-t border-gray-100">
                                                        <button
                                                            onClick={() => {
                                                                setDrawerOpen(false);
                                                                navigate(`/tracker/${rx.id || rx.rx_id}`, {
                                                                    state: {
                                                                        draftStatus: currentEdit.call_status,
                                                                        draftRemark: currentEdit.remarks
                                                                    }
                                                                });
                                                            }}
                                                            className="text-[11px] font-bold text-teal-700 hover:text-teal-900 flex items-center gap-1.5 transition bg-white border border-teal-200 hover:bg-teal-50 hover:border-teal-300 px-3 py-1.5 rounded-lg shadow-sm"
                                                        >
                                                            <Activity size={13} /> View Tracker Details
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Drawer Footer Sticky Save */}
                        <div className="p-4 border-t border-gray-200 bg-white shadow-md">
                            <button
                                onClick={handleSaveChanges}
                                disabled={drawerSaving}
                                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 uppercase tracking-wider text-xs transition duration-200 shadow-md focus:outline-none"
                            >
                                {drawerSaving ? (
                                    <>
                                        <Loader2 className="animate-spin" size={16} /> Saving Changes...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} /> Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default Tracker;